import AccountApiRequest from '@/api/account'
import ErrorType from '@/enum/ErrorType'
import StatusCodeType from '@/enum/StatusCodeType'
import ResponseTypeMapper from '@/mappers/ResponseMapper'
import { User } from '@/models'
import { clearStorageAndRefresh } from '@/services/AuthService'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import type { TPermission } from '@/types/api-types/account'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import { DASHBOARD_CLIENT_TYPE, WORKER_SIGNAL_R } from '@/utils/constants'
import {
  clearLocalStorageAndCookies,
  saveDataToCookieStorage,
  saveUserIdToLocalStorage
} from '@/utils/helpers'
import { logError } from '@/utils/helpers/logger'
import { defineStore } from 'pinia'
import useGlobalStore from '../global'
import getters from './getters'
import state from './state'
import useWorkersStore from '@/stores/modules/workers'
import { MainToWorkerMessageType } from '@/enum/SignalRWorkerMessageType'
import SignalRGroupType from '@/enum/SignalRGroupType'

const useAccountStore = defineStore('account', {
  state: () => state,
  getters,
  actions: {
    async mockLoginUser(email: string, password: string) {
      let mockData: any = {}

      const storeUserMockData = () => {
        mockData = {
          userId: 'mockUserId',
          accessToken: 'mockAccessToken',
          accessTokenExpiresInSeconds: 99999,
          refreshToken: 'mockRefreshToken',
          refreshTokenExpiresInSeconds: 99999,
          userRole: 'Admin',
          accessTokenExpirationUtc: '2025-09-17T12:15:18.249Z',
          refreshTokenExpirationUtc: '2025-09-17T12:15:18.249Z'
        }

        saveDataToCookieStorage(mockData)
        saveUserIdToLocalStorage(mockData.userId)

        const globalStore = useGlobalStore()
        globalStore.setAccountData(mockData)
      }

      return new Promise((resolve) => {
        const timeoutID = setTimeout(() => {
          storeUserMockData()
          resolve({
            status: StatusCodeType.Success,
            data: mockData
          })
          clearTimeout(timeoutID)
        }, 0)
      })
    },
    loginUser(email: string, password: string): TApiResponse | undefined {
      let response
      const accountApi = new AccountApiRequest()
      try {
        response = accountApi.requestAccess({
          username: email,
          password,
          clientAppKey: DASHBOARD_CLIENT_TYPE
        })
      } catch (error) {
        logError('Error during login: ', error)
      }

      return response
    },
    clearGlobalStores() {
      const globalStore = useGlobalStore()
      // const permissionsStore = usePermissionsStore();
      // const overviewStore = useOverviewStore();

      globalStore.$reset()
      // permissionsStore.$reset();
      // overviewStore.$reset();
    },
    async unsubscribeToJobActivityUpdates() {
      const workersStore = useWorkersStore()

      const signalRWorker = workersStore.workers?.[WORKER_SIGNAL_R]

      if (!signalRWorker) return
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Unsubscribe,
        channels: [SignalRGroupType.JobActivity]
      })
    },
    async logoutUser() {
      this.unsubscribeToJobActivityUpdates()
      const accountApi = new AccountApiRequest()
      const formData = new FormData()
      formData.append('clientAppKey', DASHBOARD_CLIENT_TYPE)
      const response = await accountApi.revokeAccess(formData as any).catch((error: any) => {
        logError('Error during logout: ', error)
      })
      clearStorageAndRefresh({ preserveItemsList: ['version'] })
      return response
    },
    clearNamespacedStores(storeName: string) {
      return new Promise((resolve) => {
        // const listStore = usePaginationStore(storeName);
        // listStore.$reset();
        resolve(true)
      })
    },
    async getAccountPermissions() {
      const accountApi = new AccountApiRequest(true)
      const response = await accountApi.getPermissions()
      if (response && response.status === StatusCodeType.Unauthorized) return
      if (response) {
        const mappedResponse = ResponseTypeMapper.CollectionResponseMapper<TPermission>(response)
      }
    },
    async getProfile(id: string) {
      const accountApi = new AccountApiRequest(true)
      try {
        const responsePromise = accountApi.getProfile(id)
        this.profilePromise = responsePromise
        const response = await responsePromise
        if (response && response.status === StatusCodeType.Success && response.data) {
          this.profile = new User(response.data)
        } else {
          console.error('User not logged in!')
        }
      } catch (error: any) {
        ErrorHandlerService.handleError({ name: error.message }, ErrorType.Component).catch(
          (error) => error
        )
        await this.logoutUser()
      }
    },
    async getValidationConfig() {
      const globalStore = useGlobalStore()
      // const configApi = new ConfigsApiRequest(true);
      // const response = await configApi.getValidations();
      // if (response && response.status === StatusCodeType.Success && response.data) {
      //   const config = new ValidationConfig(response.data);
      //   globalStore.validationConfig = config;

      //   if (process.env.NODE_ENV === 'development') {
      //     globalStore.validationConfig.patrolSettingsDefault.expectedTimeInMinutes.default = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.expectedTimeInMinutes.min = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.surveySendTimeInMinutes.default = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.surveySendTimeInMinutes.min = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.defaultMinimumTimeInMinutes = 0;
      //   }
      // }
    },
    async requestPasswordReset(email: string) {
      const accountApi = new AccountApiRequest()
      const requestBody = new FormData()
      requestBody.set('email', email)
      requestBody.set('clientAppKey', DASHBOARD_CLIENT_TYPE)
      const result = await accountApi.resetPassword(requestBody)
      return result
    },
    async confirmNewPassword(email: string, password: string, token: string) {
      const accountApi = new AccountApiRequest()
      const requestBody = new FormData()
      requestBody.set('email', email)
      requestBody.set('newPassword', password)
      requestBody.set('token', token)
      requestBody.set('clientAppKey', DASHBOARD_CLIENT_TYPE)
      const result = await accountApi.confirmNewPassword(requestBody)
      return result
    },
    resetAndClearApp() {
      clearLocalStorageAndCookies({ preserveItemsList: ['version'] })
    }
  }
})

export default useAccountStore
